import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, Snackbar } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { userService } from "../../services/UserService";
import Skeleton from "@mui/material/Skeleton";
import LinearWithValueLabel from "../common/LinearProgress";
import * as fs from "file-saver";

function RegisterdUsersInfo() {
  const [t] = useTranslation();
  const [registeredUserTotalCount, setRegisteredUserTotalCount] =
    useState<string>("0");
  const [registeredUserLast30Days, setRegisteredUserLast30Days] =
    useState<string>("0");
  const [userRegisteredToday, setUserRegisteredToday] = useState<string>("0");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalText, setModalText] = useState<string>("");
  const [modalType, setModalType] = useState<string>("error");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [downloadDialog, setDownloadDialog] = useState<boolean>(false);

  useEffect(() => {
    fetchRegisteredUserCount();
  }, []);

  const handleModelClose = () => setShowModal(false);

  const fetchRegisteredUserCount = async () => {
    setIsLoading(true);
    const res = await userService.getRegisterdUserCount();
    if (res) {
      setRegisteredUserTotalCount(res.totalRegisteredUsers);
      setRegisteredUserLast30Days(res.registeredIn30Days);
      setUserRegisteredToday(res.registeredToday);
    } else {
      setModalType("error");
      setModalText(t("Something went wrong!"));
      setShowModal(true);
    }
    setIsLoading(false);
  };

  const handleAllUsersExport = async () => {
    setProgress(0);
    setDownloadDialog(true);
    let totalData: any = [];
    let total = 1000;
    let recordsToSkip = 0;
    let takeItems = total > 1000 ? 1000 : total;
    let totalFetchedItems = 0;
    let res: any;

    while (totalFetchedItems < total) {
      res = await userService.getRegisteredUsers(recordsToSkip, takeItems);
      if (res && res.registeredUsers && res.registeredUsers.length > 0) {
        total = res.total;
        totalFetchedItems += res.registeredUsers
          ? res.registeredUsers.length
          : 0;
        recordsToSkip += takeItems;
        if (res.registeredUsers && res.registeredUsers.length > 0) {
          for (let i = 0; i < res.registeredUsers.length; i++) {
            totalData.push(res.registeredUsers[i]);
          }
        }
        let progressValue = (totalFetchedItems / total) * 100;
        setProgress(progressValue > 100 ? 100 : progressValue);
      }
    }

    if (res && res.registeredUsers && res.registeredUsers.length > 0) {
      let headers: string[] = [
        "Name",
        "Email",
        "Mobile Number",
        "Registration Date",
      ];
      const ExcelJS = require("exceljs");
      const workbook = new ExcelJS.Workbook();
      const dataForExcel = [];
      for (let i = 0; i < totalData.length; i++) {
        const item = totalData[i];
        const field = {
          name: item.firstName + " " + item.lastName,
          email: item.email,
          mobileNo: item.mobileNumber,
          registeredOn: new Date(item.registeredOn).toDateString(),
        };
        dataForExcel.push(
          Object.values(field !== undefined && field !== null ? field : "")
        );
      }

      // Frozen worksheet
      const worksheet = workbook.addWorksheet("List Of Registered Users");

      //Adding Header Row
      let headerRow = worksheet.addRow(headers);
      headerRow.eachCell((cell: any, number: any) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "4167B8" },
          bgColor: { argb: "" },
        };
        cell.font = {
          bold: true,
          color: { argb: "FFFFFF" },
          size: 12,
        };
      });

      dataForExcel.forEach((d) => {
        worksheet.addRow(d);
      });

      autoSize(worksheet, 1, 5);

      workbook.xlsx.writeBuffer().then((data: any) => {
        let blob = new Blob([data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        fs.saveAs(blob, "Registered Users.xlsx");
      });
    }
    setDownloadDialog(false);
  };

  // Auto width for column
  const autoSize = (sheet: any, fromRow: number, PixelPerColumn: number) => {
    const PIXELS_PER_EXCEL_WIDTH_UNIT = PixelPerColumn;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    const maxColumnLengths: Array<number> = [];
    sheet.eachRow((row: any, rowNum: any) => {
      if (rowNum < fromRow) {
        return;
      }

      row.eachCell((cell: any, num: any) => {
        if (typeof cell.value === "string" || typeof cell.value === "number") {
          if (maxColumnLengths[num] === undefined) {
            maxColumnLengths[num] = 0;
          }

          const fontSize = cell.font && cell.font.size ? cell.font.size : 11;
          ctx.font = `${fontSize}pt Arial`;
          const metrics = ctx.measureText(cell.value);
          const cellWidth = metrics.width;

          maxColumnLengths[num] = Math.max(maxColumnLengths[num], cellWidth);

          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        }
      });
    });

    for (let i = 1; i <= sheet.columnCount; i++) {
      const col = sheet.getColumn(i);
      const width = maxColumnLengths[i];
      if (width) {
        col.width = width / PIXELS_PER_EXCEL_WIDTH_UNIT + 1;
      }
    }
  };

  return (
    <>
      <div className="card border-0 shadow-sm rounded-1rem">
        <div className="card-body p-4">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="fw-bold text-primary">{t("Registered Users")}</h6>
            <button
              className="btn btn-primary btn-sm no-focus"
              onClick={() => handleAllUsersExport()}
            >
              {t("Export Users")}
            </button>
          </div>

          <div className="p-4 skeleton">
            <div className="row">
              <div className="col-8">
                {isLoading ? (
                  <Skeleton variant="text" sx={{ fontSize: "3rem" }} />
                ) : (
                  <p className="fs-18 bg-success bg-opacity-10 border-0 rounded-10 p-2">
                    {t("Total Registered User")}
                  </p>
                )}
              </div>
              <div className="col-4">
                {isLoading ? (
                  <Skeleton variant="text" sx={{ fontSize: "3rem" }} />
                ) : (
                  <p className="fs-18 bg-success bg-opacity-10 border-0 rounded-10 p-2 fw-600">
                    {registeredUserTotalCount}
                  </p>
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-8">
                {isLoading ? (
                  <Skeleton variant="text" sx={{ fontSize: "3rem" }} />
                ) : (
                  <p className="fs-18 bg-info bg-opacity-10 border-0 rounded-10 p-2">
                    {t("Users Registered In Last 30 Days")}
                  </p>
                )}
              </div>
              <div className="col-4">
                {isLoading ? (
                  <Skeleton variant="text" sx={{ fontSize: "3rem" }} />
                ) : (
                  <p className="fs-18 bg-info bg-opacity-10 border-0 rounded-10 p-2 fw-600">
                    {registeredUserLast30Days}
                  </p>
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-8">
                {isLoading ? (
                  <Skeleton variant="text" sx={{ fontSize: "3rem" }} />
                ) : (
                  <p className="fs-18 bg-warning bg-opacity-10 border-0 rounded-10 p-2">
                    {t("Users Registered Today")}
                  </p>
                )}
              </div>
              <div className="col-4">
                {isLoading ? (
                  <Skeleton variant="text" sx={{ fontSize: "3rem" }} />
                ) : (
                  <p className="fs-18 bg-warning bg-opacity-10 border-0 rounded-10 p-2 fw-600">
                    {userRegisteredToday}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        fullWidth={true}
        maxWidth="sm"
        open={downloadDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent className="my-3">
          <div className="d-flex justify-content-center align-items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="75"
              width="75"
              viewBox="0 0 512 512"
              className="text-center"
            >
              <path d="M256 0a256 256 0 1 0 0 512A256 256 0 1 0 256 0zM244.7 395.3l-112-112c-4.6-4.6-5.9-11.5-3.5-17.4s8.3-9.9 14.8-9.9l64 0 0-96c0-17.7 14.3-32 32-32l32 0c17.7 0 32 14.3 32 32l0 96 64 0c6.5 0 12.3 3.9 14.8 9.9s1.1 12.9-3.5 17.4l-112 112c-6.2 6.2-16.4 6.2-22.6 0z" />
            </svg>
          </div>

          <h6 className="fw-600 text-center my-3">
            {t(
              "Exporting users list, please do not close or refresh the page."
            )}
          </h6>
          <LinearWithValueLabel exportValue={progress}></LinearWithValueLabel>
        </DialogContent>
      </Dialog>

      {modalType === "error" && (
        <Snackbar
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          open={showModal}
          autoHideDuration={3000}
          onClose={handleModelClose}
        >
          <Alert onClose={handleModelClose} severity="error">
            {modalText}
          </Alert>
        </Snackbar>
      )}
    </>
  );
}

export default RegisterdUsersInfo;
